import { useState, useEffect } from 'react';
import {
  collection,
  query,
  where,
  getDocs,
  addDoc,
  deleteDoc,
  doc,
  updateDoc,
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { useSlices } from '../hooks/useSlices';
import { Pizza } from '../types';

export function useMenuPizzas(pizzeriaId: string) {
  const [menuPizzas, setMenuPizzas] = useState<
    Array<{
      menuItemId: string;
      pizza: Pizza;
      daysRemaining: number;
      isHidden: boolean;
    }>
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const { reloadBalance, purchaseSlices } = useSlices();

  const loadMenuPizzas = async () => {
    try {
      setLoading(true);
      const menuRef = collection(db, 'pizzeinmenu');
      const q = query(menuRef, where('pizzeriaId', '==', pizzeriaId));
      const menuSnapshot = await getDocs(q);

      const pizzasPromises = menuSnapshot.docs.map(async (menuDoc) => {
        const menuData = menuDoc.data();
        const pizzaRef = doc(db, 'pizze', menuData.pizzaId);
        const pizzaDoc = await getDocs(
          query(
            collection(db, 'pizze'),
            where('__name__', '==', menuData.pizzaId)
          )
        );

        if (pizzaDoc.empty) return null;

        const pizzaData = pizzaDoc.docs[0].data();
        const addedDate = new Date(menuData.addedAt);
        const now = new Date();
        const diffTime = Math.abs(now.getTime() - addedDate.getTime());
        const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
        const daysRemaining = 30 - diffDays;

        return {
          menuItemId: menuDoc.id,
          pizza: { id: pizzaDoc.docs[0].id, ...pizzaData } as Pizza,
          daysRemaining: Math.max(0, daysRemaining),
          isHidden: menuData.isHidden || false,
        };
      });

      const pizzas = (await Promise.all(pizzasPromises)).filter(Boolean);
      setMenuPizzas(pizzas.filter((pizza): pizza is { menuItemId: string; pizza: Pizza; daysRemaining: number; isHidden: boolean; } => 
        pizza !== null
      ));
      setError(null);
    } catch (err) {
      console.error('Errore nel caricamento del menu:', err);
      setError('Errore nel caricamento del menu');
    } finally {
      setLoading(false);
    }
  };

  const addPizzaToMenu = async (pizzaId: string) => {
    try {
      const menuRef = collection(db, 'pizzeinmenu');
      const now = new Date();
      const expiresAt = new Date(now.getTime() + 30 * 24 * 60 * 60 * 1000);

      await addDoc(menuRef, {
        pizzeriaId,
        pizzaId,
        addedAt: now.toISOString(),
        expiresAt: expiresAt.toISOString(),
        isHidden: false,
      });

      await Promise.all([loadMenuPizzas(), reloadBalance()]); // Ricarica sia il menu che il saldo
      return true;
    } catch (err) {
      console.error("Errore nell'aggiunta della pizza:", err);
      throw err;
    }
  };

  const removePizzaFromMenu = async (menuItemId: string) => {
    try {
      const menuItemRef = doc(db, 'pizzeinmenu', menuItemId);
      await deleteDoc(menuItemRef);
      await Promise.all([
        loadMenuPizzas(),
        reloadBalance()
      ]);
      return true;
    } catch (err) {
      console.error('Errore nella rimozione della pizza:', err);
      throw err;
    }
  };

  const togglePizzaVisibility = async (
    menuItemId: string,
    isHidden: boolean
  ) => {
    try {
      const menuItemRef = doc(db, 'pizzeinmenu', menuItemId);
      await updateDoc(menuItemRef, { isHidden });
      await loadMenuPizzas();
      return true;
    } catch (err) {
      console.error('Errore nel cambio di visibilità:', err);
      throw err;
    }
  };

  useEffect(() => {
    if (pizzeriaId) {
      loadMenuPizzas();
    }
  }, [pizzeriaId]);

  return {
    menuPizzas,
    loading,
    error,
    addPizzaToMenu,
    removePizzaFromMenu,
    togglePizzaVisibility,
    reloadMenu: loadMenuPizzas,
  };
}
